import { Dispatch } from 'react';
import { Observable, Subject } from 'rxjs';
import { UserContextAction } from '../components/app/user-context';
import { ILabel } from '../rs-emd-ui-modules/src/models/common/label.model';
import { IPartsListService } from '../rs-emd-ui-modules/src/services/parts-list/parts-list-service';
import { IAddToPartsListRequest, IAddToPartsListResult } from '../rs-emd-ui-modules/src/services/parts-list/parts-list.dto';
import {
  AddToPartsListV3Document,
  AddToPartsListV3Mutation,
  AddToPartsListV3MutationVariables,
  GetPartsListLabelsDocument,
  GetPartsListLabelsQuery,
  GetPartsListLabelsQueryVariables,
  PartsListDocument,
  PartsListProductPriceArea,
  PartsListProductsDocument,
  PartsListProductsQuery,
  PartsListProductsQueryVariables,
  PartsListQuery,
  PartsListQueryVariables,
  UpdatePartsListLineDocument,
  UpdatePartsListLineMutation,
  UpdatePartsListLineMutationVariables,
} from './../generated/graphql';
import { PartsListQueryType, PartsListRequest, PartsListResponse } from './../rs-emd-ui-modules/src/components/parts-list/parts-list.dto';
import { graphQLService } from './graphql-service';
import { headerService } from './header-service';

export class PartsListService implements IPartsListService {
  public responseObservable: Observable<PartsListResponse>;

  constructor(
    readonly requestSubject = new Subject<PartsListRequest>(),
    private readonly responseSubject = new Subject<PartsListResponse>()
  ) {
    this.responseObservable = this.responseSubject.asObservable();

    this.watchRequestSubject();
  }

  watchRequestSubject() {
    this.requestSubject.subscribe((req) => {
      switch (req.type) {
        case PartsListQueryType.GetPartsList:
          this.getPartsList(req.contextStockCode);
          return;
        case PartsListQueryType.GetPartsListProducts:
          this.getPartsListProducts(req.partsListId);
          return;
      }
    });
  }

  getPartsList(stockCode?: string) {
    graphQLService.apolloClient
      .query<PartsListQuery, PartsListQueryVariables>({
        query: PartsListDocument,
        variables: { contextStockCode: stockCode },
        fetchPolicy: 'network-only',
      })
      .then((result) => {
        result.data &&
          this.responseSubject.next({
            type: PartsListQueryType.GetPartsList,
            data: result.data.partsListPage,
          });
      });
  }

  getPartsListProducts(partsListId: number) {
    graphQLService.apolloClient
      .query<PartsListProductsQuery, PartsListProductsQueryVariables>({
        query: PartsListProductsDocument,
        variables: { partsListId: partsListId },
        fetchPolicy: 'network-only',
      })
      .then((result) => {
        result.data &&
          this.responseSubject.next({
            type: PartsListQueryType.GetPartsListProducts,
            data: result.data.partsListPage,
          });
      });
  }

  async addToPartsList(
    data: IAddToPartsListRequest,
    userDispatchCallback: Dispatch<UserContextAction> | null
  ): Promise<IAddToPartsListResult> {
    const result = await graphQLService.apolloClient.mutate<AddToPartsListV3Mutation, AddToPartsListV3MutationVariables>({
      mutation: AddToPartsListV3Document,
      variables: data.variables,
    });

    if (result.errors) {
      window.location.href = process.env.REACT_APP_ERROR_ENDPOINT ?? '';
    } else {
      // update header partslist totals if a new partslist was added
      if (result.data?.addToPartsListV3.isSuccess && data.variables.data.partsListName)
        headerService.getPartsListsTotal(userDispatchCallback);
    }

    return result;
  }

  async updatePartsListLine(partsListId: number, partsListLineId: number, quantity: number): Promise<PartsListProductPriceArea> {
    const result = await graphQLService.apolloClient.mutate<UpdatePartsListLineMutation, UpdatePartsListLineMutationVariables>({
      mutation: UpdatePartsListLineDocument,
      variables: { input: { partsListId, partsListLineId, quantity } },
    });

    if (result.errors) {
      window.location.href = process.env.REACT_APP_ERROR_ENDPOINT ?? '';
    }

    return result.data?.updatePartsListLine.priceArea as PartsListProductPriceArea;
  }

  async getPartsListLabels(): Promise<ILabel[]> {
    const result = await graphQLService.apolloClient.query<GetPartsListLabelsQuery, GetPartsListLabelsQueryVariables>({
      query: GetPartsListLabelsDocument,
    });

    return result.data.getPartsListLabels.labels;
  }
}

export const partsListService = new PartsListService();
